import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import StandardModal from './StandardModal';
import { PrimaryButton, SecondaryButton } from './Buttons';
import RoleSelector from './RoleSelector';
import { PhoneInput } from './PhoneInput';
import { useRoles } from '../hooks/useData';
import useModalPersistence from '../hooks/useModalPersistence';
import { 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  User, 
  Users, 
  Key, 
  Eye,
  X,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { orgApi } from '../utils/apiV2';

const AddTeamModal = ({ isOpen, onClose, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const { roles } = useRoles(); // Use useSWR instead of manual fetching
  const [usernameAvailable, setUsernameAvailable] = useState(null); // null, true, false
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null); // null, true, false
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Modal persistence
  const initialData = {
    name: '',
    email: '',
    phone: '',
    roleId: '',
    roleName: '',
    username: '',
    password: '',
    sendInvite: true,  // Default to sending invite email
    doubleDeckerCertified: false,
    recurringAvailability: [],
    unavailableDates: [],
    schedulerWeight: 3
  };
  
  const { formData, setFormData, clearDraft } = useModalPersistence('draft_addTeam', initialData, isOpen);

  const totalSteps = 4;

  // Re-validate when modal opens with existing data
  useEffect(() => {
    if (!isOpen) return;
    
    // Reset to step 1 when modal opens
    setCurrentStep(1);
    
    // Small delay to ensure formData is loaded from persistence
    const validateTimeout = setTimeout(() => {
      // Re-validate email if draft was restored with valid email
      if (formData.email && formData.email.includes('@')) {
        setEmailAvailable(null);
        setCheckingEmail(true);
        
        const checkEmail = async () => {
          try {
            const orgId = localStorage.getItem('organizationId');
            const response = await api.get(`/v2/organizations/${orgId}/team-members/check-email/${encodeURIComponent(formData.email)}`);
            setEmailAvailable(response.data.available);
            setUsernameAvailable(response.data.available);
          } catch (error) {
            console.error('Error checking email on modal open:', error);
            setEmailAvailable(true); // Fail-open
            setUsernameAvailable(true);
          } finally {
            setCheckingEmail(false);
          }
        };
        checkEmail();
      } else {
        // Reset validation state for empty/invalid email
        setEmailAvailable(null);
        setUsernameAvailable(null);
      }
    }, 100); // Small delay to allow formData to be populated from storage
    
    return () => clearTimeout(validateTimeout);
  }, [isOpen, formData.email]);

  const resetForm = () => {
    setCurrentStep(1);
    setFormData(initialData);
    clearDraft();
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If updating email, also update username (email is the username)
    if (field === 'email') {
      setFormData(prev => ({ ...prev, [field]: value, username: value }));
    }
    
    // If updating email, debounce check against v2 API
    if (field === 'email' && value && value.includes('@')) {
      setEmailAvailable(null);
      setCheckingEmail(true);
      setUsernameAvailable(null); // Username = email, so same availability
      
      if (window.emailCheckTimeout) {
        clearTimeout(window.emailCheckTimeout);
      }
      
      window.emailCheckTimeout = setTimeout(async () => {
        try {
          // Get org_id from localStorage
          const orgId = localStorage.getItem('organizationId');
          const response = await api.get(`/v2/organizations/${orgId}/team-members/check-email/${encodeURIComponent(value)}`);
          setEmailAvailable(response.data.available);
          setUsernameAvailable(response.data.available); // Same as email
        } catch (error) {
          console.error('Error checking email:', error);
          setEmailAvailable(true); // Fail-open
          setUsernameAvailable(true);
        } finally {
          setCheckingEmail(false);
        }
      }, 500);
    }
  };

  const nextStep = () => {
    if (validateCurrentStep()) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        if (!formData.name.trim()) {
          toast.error('Please enter team member name');
          return false;
        }
        if (!formData.email.trim()) {
          toast.error('Please enter email address');
          return false;
        }
        if (!formData.email.includes('@')) {
          toast.error('Please enter a valid email');
          return false;
        }
        if (checkingEmail) {
          toast.error('Please wait while we verify email availability');
          return false;
        }
        if (emailAvailable !== true) {
          toast.error('Please use an available email address');
          return false;
        }
        if (!formData.phone.trim()) {
          toast.error('Please enter phone number');
          return false;
        }
        return true;
      case 2:
        if (!formData.roleId) {
          toast.error('Please select a role');
          return false;
        }
        return true;
      case 3:
        // Username is auto-populated from email, so just check email was provided
        if (!formData.email) {
          toast.error('Please go back and enter an email address');
          return false;
        }
        if (emailAvailable === false) {
          toast.error('Email already in use - please use a different email');
          return false;
        }
        // Only validate password if NOT sending invite
        if (formData.sendInvite !== true) {
          if (!formData.password) {
            toast.error('Please enter password');
            return false;
          }
          // Enforce strong password requirements
          if (formData.password.length < 8 ||
              !/[a-z]/.test(formData.password) ||
              !/[A-Z]/.test(formData.password) ||
              !/[0-9]/.test(formData.password) ||
              !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
            toast.error('Please meet all password requirements');
            return false;
          }
        }
        return true;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        username: formData.email?.trim() || formData.username?.trim(),  // Use email as username
        password: formData.sendInvite === true ? null : formData.password,  // Only send password if not sending invite
        roleId: formData.roleId,
        roleName: formData.roleName,
        recurringAvailability: formData.recurringAvailability,
        unavailableDates: formData.unavailableDates,
        payRate: 20.0,
        schedulerWeight: formData.schedulerWeight,
        doubleDeckerCertified: formData.doubleDeckerCertified
      };

      await orgApi.createTeamMember(payload);
      if (formData.sendInvite === true) {
        toast.success(`🎉 ${formData.name} added! Invite email sent to ${formData.email}`);
      } else {
        toast.success(`🎉 ${formData.name} added successfully!`);
      }
      clearDraft(); // Clear draft on success
      onSuccess(); // Refresh team list
      onClose(); // Close modal
    } catch (error) {
      console.error('Error adding teamMember:', error);
      
      let errorMessage = 'Failed to add teamMember';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (detail?.code === 'OWNER_EMAIL_CONFLICT') {
          errorMessage = detail.message;
        } else if (Array.isArray(detail)) {
          const validationErrors = detail.map(err =>
            `${err.loc?.[err.loc.length - 1] || 'Field'}: ${err.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        } else if (detail?.message) {
          errorMessage = detail.message;
        }
      }
      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Basic Information</h2>
              <p className="text-gray-600 text-sm mt-1">Start with the team member&apos;s details</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => updateFormData('name', e.target.value)}
                  placeholder="Enter teamMember's full name"
                  className="mt-1 h-11"
                />
              </div>

              <div>
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email Address <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateFormData('email', e.target.value)}
                    placeholder="team@example.com"
                    className="mt-1 h-11 pr-10"
                  />
                  {formData.email && formData.email.includes('@') && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {checkingEmail ? (
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                      ) : emailAvailable === true ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : emailAvailable === false ? (
                        <X className="w-4 h-4 text-red-600" />
                      ) : null}
                    </div>
                  )}
                </div>
                {emailAvailable === false && (
                  <p className="text-xs text-red-600 mt-1">Email already in use in this organization</p>
                )}
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number <span className="text-red-500">*</span></Label>
                <PhoneInput
                  id="phone"
                  value={formData.phone}
                  defaultCountry={(localStorage.getItem('organizationCountry') === 'UAE' || localStorage.getItem('organizationCountry') === 'AE') ? 'AE' : 'US'}
                  onChange={(e164) => updateFormData('phone', e164)}
                  data-testid="team-member-phone-input"
                  placeholder="Phone number"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900">Assign Role</h2>
              <p className="text-gray-600 text-sm mt-1">What role will {formData.name || 'this teamMember'} have?</p>
            </div>

            <div>
              <Label className="text-sm font-medium mb-2 block">Select Role <span className="text-red-500">*</span></Label>
              <RoleSelector
                roles={roles}
                value={formData.roleId}
                onChange={(roleId) => {
                  const role = roles.find(r => r.id === roleId);
                  updateFormData('roleId', roleId);
                  updateFormData('roleName', role?.name || '');
                }}
                placeholder="Choose a role..."
                showAddButton={true}
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Key className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Login Credentials</h2>
              <p className="text-gray-600 text-sm mt-1">Set up login access for {formData.name || 'this teamMember'}</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                  Username <span className="text-gray-400 text-xs font-normal">(auto-generated from email)</span>
                </Label>
                <div className="flex gap-2 mt-1">
                  <div className="relative flex-1">
                    <Input
                      id="username"
                      value={formData.username || formData.email}
                      disabled
                      placeholder="Email will be used as username"
                      className="h-11 bg-gray-100 text-gray-600 cursor-not-allowed"
                    />
                    {/* Validation Feedback */}
                    {formData.email && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center">
                        {checkingEmail ? (
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        ) : emailAvailable === true ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : emailAvailable === false ? (
                          <X className="w-5 h-5 text-red-600" />
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.email ? `Team member will login with: ${formData.email}` : 'Enter email in step 1 to set username'}
                </p>
              </div>

              <div>
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  Password Setup
                </Label>
                
                {/* Password Mode Selection */}
                <div className="mt-2 space-y-2">
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={formData.sendInvite === true}
                      onChange={() => {
                        updateFormData('sendInvite', true);
                        updateFormData('password', '');
                      }}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Send Invite Email</span>
                      <p className="text-xs text-gray-500">Team member will receive an email to set their own password</p>
                    </div>
                  </label>
                  
                  <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="passwordMode"
                      checked={formData.sendInvite !== true}
                      onChange={() => updateFormData('sendInvite', false)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <div>
                      <span className="font-medium text-gray-900">Set Password Now</span>
                      <p className="text-xs text-gray-500">You provide the password and share it with the team member</p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Password Input - Only show if not sending invite */}
              {formData.sendInvite !== true && (
                <>
                  <div>
                    <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => updateFormData('password', e.target.value)}
                      placeholder="Create a strong password"
                      className="mt-1 h-11"
                    />
                  </div>

                  {/* Password Requirements Checklist */}
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-semibold text-gray-700 mb-2">Password Requirements:</p>
                    
                    <div className="flex items-center gap-2">
                      {formData.password.length >= 8 ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                      <span className={`text-xs ${formData.password.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>8+ characters</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/[a-z]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                      <span className={`text-xs ${/[a-z]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 lowercase letter</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/[A-Z]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                      <span className={`text-xs ${/[A-Z]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 uppercase letter</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/[0-9]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                      <span className={`text-xs ${/[0-9]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 number</span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? <Check className="w-3 h-3 text-green-600" /> : <X className="w-3 h-3 text-red-500" />}
                      <span className={`text-xs ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password) ? 'text-green-700' : 'text-gray-600'}`}>1 symbol (!@#$%^&*)</span>
                    </div>
                  </div>
                </>
              )}
              
              {/* Invite Email Info */}
              {formData.sendInvite === true && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    📧 An invite email will be sent to <strong>{formData.email || 'the team member'}</strong> with instructions to verify their email and set their password.
                  </p>
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Eye className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Review & Confirm</h2>
              <p className="text-gray-600 text-sm mt-1">Please review the details before adding {formData.name || 'this team member'}</p>
            </div>

            <div className="space-y-4 max-h-96 overflow-y-auto">
              {/* Basic Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Basic Information
                </h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Name:</span> {formData.name}</p>
                  {formData.email && <p><span className="font-medium">Email:</span> {formData.email}</p>}
                  {formData.phone && <p><span className="font-medium">Phone:</span> {formData.phone}</p>}
                </div>
              </div>

              {/* Role */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Role
                </h3>
                <Badge style={{ backgroundColor: roles.find(r => r.id === formData.roleId)?.color || '#3B82F6' }}>
                  {formData.roleName}
                </Badge>
              </div>

              {/* Credentials */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Login Credentials
                </h3>
                <div className="space-y-1 text-sm">
                  <p><span className="font-medium">Username:</span> {formData.username}</p>
                  <p><span className="font-medium">Access:</span> {formData.sendInvite === true ? 'Invite email will be sent' : 'Password set manually'}</p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return 'Basic Info';
      case 2: return 'Assign Role';
      case 3: return 'Login Credentials';
      case 4: return 'Review';
      default: return '';
    }
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Team Member"
      description={null}
      size="md"
      dataTestId="add-team-member-modal"
      showCloseButton={false}
      footer={null}
    >
      {/* Visual Progress Bar */}
      <div className="relative h-2 bg-gray-200 rounded-full mb-8">
        <div 
          className="absolute h-full rounded-full transition-all duration-300"
          style={{ backgroundColor: '#2563EB', width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>

      {/* Step Content */}
      <div className="min-h-[300px]" data-testid={`add-team-wizard-step-${currentStep}`}>
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between gap-3 pt-4">
        {currentStep > 1 ? (
          <SecondaryButton onClick={prevStep} disabled={isSubmitting}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </SecondaryButton>
        ) : (
          <SecondaryButton onClick={onClose} disabled={isSubmitting}>
            Cancel
          </SecondaryButton>
        )}
        
        {currentStep === totalSteps ? (
          <PrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-1" />
                Add Team Member
              </>
            )}
          </PrimaryButton>
        ) : (
          <PrimaryButton 
            onClick={nextStep}
            disabled={
              (currentStep === 1 && (
                !formData.name.trim() || 
                !formData.email.trim() || 
                !formData.phone.trim() ||
                checkingEmail || 
                emailAvailable !== true
              )) ||
              (currentStep === 3 && (
                !formData.username.trim() ||
                checkingUsername || 
                usernameAvailable !== true
              ))
            }
            className="disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight className="w-4 h-4 ml-1" />
          </PrimaryButton>
        )}
      </div>
    </StandardModal>
  );
};

export default AddTeamModal;